import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Fab,
  Badge,
  FormControlLabel,
  Switch,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AttachFile,
  Person,
  Schedule,
  Priority,
  CheckCircle,
  RadioButtonUnchecked,
  PlayArrow,
  Block,
  GetApp,
  Visibility
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

function WorkItems({ user }) {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [workItems, setWorkItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [overviewStatusMenuAnchor, setOverviewStatusMenuAnchor] = useState(null);
  const [selectedOverviewItem, setSelectedOverviewItem] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    text: '',
    notes: '',
    timing: '',
    status: 'pending',
    priority: 'medium',
    assignee_id: '',
    category: ''
  });
  const [attachments, setAttachments] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assignee_id: '',
    timing: '',
    category: '',
    hideCompleted: false
  });

  const statusOptions = [
    { value: 'pending', label: 'Pending', color: 'default', icon: <RadioButtonUnchecked /> },
    { value: 'in_progress', label: 'In Progress', color: 'primary', icon: <PlayArrow /> },
    { value: 'completed', label: 'Completed', color: 'success', icon: <CheckCircle /> },
    { value: 'blocked', label: 'Blocked', color: 'error', icon: <Block /> }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'info' },
    { value: 'medium', label: 'Medium', color: 'default' },
    { value: 'high', label: 'High', color: 'warning' },
    { value: 'urgent', label: 'Urgent', color: 'error' }
  ];

  const timingOptions = [
    { value: 'PTCC', label: 'PTCC (Prior to Construction Certificate)' },
    { value: 'PTC', label: 'PTC (Prior to Commencement)' },
    { value: 'DW', label: 'DW (During Works)' },
    { value: 'OC', label: 'OC (Occupation Certificate)' }
  ];

  const categoryOptions = [
    { value: 'Contracts, Applications & Fees', label: 'Contracts, Applications & Fees' },
    { value: 'Plans', label: 'Plans' },
    { value: 'Specifications', label: 'Specifications' },
    { value: 'Design Certificates', label: 'Design Certificates' },
    { value: 'Combustible external walls & combustible attachments to external walls', label: 'Combustible external walls & combustible attachments to external walls' },
    { value: 'Compliance Statement - Development Consent Consistency', label: 'Compliance Statement - Development Consent Consistency' },
    { value: 'Building Code of Australia Compliance (BCA)', label: 'Building Code of Australia Compliance (BCA)' },
    { value: 'Performance Solution Reports', label: 'Performance Solution Reports' },
    { value: 'Fire Protection and structural Capacity (Clause 143)', label: 'Fire Protection and structural Capacity (Clause 143)' },
    { value: 'Disability (Access to premises & buildings) standards 2010', label: 'Disability (Access to premises & buildings) standards 2010' },
    { value: 'Long Service Levy Payment (LSL)', label: 'Long Service Levy Payment (LSL)' },
    { value: 'Sydney Water Tap in Approval', label: 'Sydney Water Tap in Approval' },
    { value: 'Fire Safety Schedule', label: 'Fire Safety Schedule' },
    { value: 'Pre-approval Inspection', label: 'Pre-approval Inspection' },
    { value: 'Design Verification (residential apartment buildings)', label: 'Design Verification (residential apartment buildings)' },
    { value: 'BASIX', label: 'BASIX' },
    { value: 'Planning Agreement', label: 'Planning Agreement' },
    { value: 'Home Building Compensation Fund Insurance', label: 'Home Building Compensation Fund Insurance' }
  ];

  // Fetch data
  useEffect(() => {
    fetchWorkItems();
    fetchUsers();
  }, [applicationId, filters]);

  const fetchWorkItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.assignee_id) params.append('assignee_id', filters.assignee_id);
      if (filters.timing) params.append('timing', filters.timing);
      if (filters.category) params.append('category', filters.category);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/applications/${applicationId}/workitems/?${params}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        let filteredData = data;
        
        if (filters.hideCompleted) {
          filteredData = data.filter(item => item.status !== 'completed');
        }
        
        setWorkItems(filteredData);
      } else {
        setError('Failed to load work items');
      }
    } catch (err) {
      setError('Error loading work items');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Form handlers
  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        text: item.text,
        notes: item.notes || '',
        timing: item.timing || '',
        category: item.category || '',
        status: item.status,
        priority: item.priority,
        assignee_id: item.assignee_id || '',
        category: item.category || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        text: '',
        notes: '',
        timing: '',
        status: 'pending',
        priority: 'medium',
        assignee_id: '',
        category: ''
      });
    }
    setAttachments([]);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({
      title: '',
      text: '',
      notes: '',
      timing: '',
      status: 'pending',
      priority: 'medium',
      assignee_id: ''
    });
    setAttachments([]);
  };

  const handleSubmit = async () => {
    try {
      const formDataObj = new FormData();
      formDataObj.append('title', formData.title);
      formDataObj.append('text', formData.text);
      if (formData.notes) formDataObj.append('notes', formData.notes);
      if (formData.timing) formDataObj.append('timing', formData.timing);
      formDataObj.append('status', formData.status);
      formDataObj.append('priority', formData.priority);
      if (formData.assignee_id) formDataObj.append('assignee_id', formData.assignee_id);
      if (formData.category) formDataObj.append('category', formData.category);
      
      attachments.forEach(file => {
        formDataObj.append('attachments', file);
      });

      const url = editingItem 
        ? `${process.env.REACT_APP_API_URL}/applications/${applicationId}/workitems/${editingItem.id}`
        : `${process.env.REACT_APP_API_URL}/applications/${applicationId}/workitems/`;
      
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        body: formDataObj,
        credentials: 'include'
      });

      if (response.ok) {
        handleCloseDialog();
        fetchWorkItems();
      } else {
        setError('Failed to save work item');
      }
    } catch (err) {
      setError('Error saving work item');
    }
  };

  const handleQuickStatusUpdate = async (newStatus) => {
    if (!selectedItem) return;
    
    setUpdatingStatus(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/applications/${applicationId}/workitems/${selectedItem.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus
          }),
          credentials: 'include'
        }
      );

      if (response.ok) {
        // Update the selected item locally
        setSelectedItem(prev => ({ ...prev, status: newStatus }));
        // Refresh the work items list
        fetchWorkItems();
      } else {
        setError('Failed to update status');
      }
    } catch (err) {
      setError('Error updating status');
    } finally {
      setUpdatingStatus(false);
      setStatusMenuAnchor(null);
    }
  };

  const handleOverviewStatusUpdate = async (newStatus) => {
    if (!selectedOverviewItem) return;
    
    setUpdatingStatus(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/applications/${applicationId}/workitems/${selectedOverviewItem.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus
          }),
          credentials: 'include'
        }
      );

      if (response.ok) {
        // Refresh the work items list
        fetchWorkItems();
      } else {
        setError('Failed to update status');
      }
    } catch (err) {
      setError('Error updating status');
    } finally {
      setUpdatingStatus(false);
      setOverviewStatusMenuAnchor(null);
      setSelectedOverviewItem(null);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/applications/${applicationId}/workitems/${itemToDelete.id}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        fetchWorkItems();
      } else {
        setError('Failed to delete work item');
      }
    } catch (err) {
      setError('Error deleting work item');
    }
  };

  // Utility functions
  const getStatusIcon = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.icon : <RadioButtonUnchecked />;
  };

  const getPriorityColor = (priority) => {
    const option = priorityOptions.find(opt => opt.value === priority);
    return option ? option.color : 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return null;
    }
  };

  const isOverdue = (dueDateString) => {
    if (!dueDateString) return false;
    try {
      return parseISO(dueDateString) < new Date();
    } catch {
      return false;
    }
  };

  const getItemCounts = () => {
    return statusOptions.reduce((acc, status) => {
      acc[status.value] = workItems.filter(item => item.status === status.value).length;
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh", background: "#f7fafc" }}>
        {/* Left menu */}
        <Box sx={{ width: 220, background: "#f5f5f5", p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Designer Portal</Typography>
          <Button fullWidth sx={{ mb: 1 }} onClick={() => navigate(`/applications/${applicationId}`)}>
            Dashboard
          </Button>
          <Button fullWidth sx={{ mb: 1 }}>My Projects</Button>
          <Button fullWidth sx={{ mb: 1 }}>Messages</Button>
          <Button fullWidth sx={{ mb: 1 }}>Settings</Button>
          <Button 
            fullWidth 
            sx={{ mb: 1, backgroundColor: '#e3f2fd', color: '#1976d2' }}
          >
            Work Items
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => navigate(`/applications/${applicationId}`)}
          >
            Back to Application
          </Button>
        </Box>
        <Box sx={{ flex: 1, p: 4 }}>
          <Container maxWidth="lg" sx={{ mt: 2 }}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
              <CircularProgress />
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh", background: "#f7fafc" }}>
        {/* Left menu */}
        <Box sx={{ width: 220, background: "#f5f5f5", p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Designer Portal</Typography>
          <Button fullWidth sx={{ mb: 1 }} onClick={() => navigate(`/applications/${applicationId}`)}>
            Dashboard
          </Button>
          <Button fullWidth sx={{ mb: 1 }}>My Projects</Button>
          <Button fullWidth sx={{ mb: 1 }}>Messages</Button>
          <Button fullWidth sx={{ mb: 1 }}>Settings</Button>
          <Button 
            fullWidth 
            sx={{ mb: 1, backgroundColor: '#e3f2fd', color: '#1976d2' }}
          >
            Work Items
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => navigate(`/applications/${applicationId}`)}
          >
            Back to Application
          </Button>
        </Box>
        <Box sx={{ flex: 1, p: 4 }}>
          <Container maxWidth="lg" sx={{ mt: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button onClick={() => navigate(-1)}>
              Go Back
            </Button>
          </Container>
        </Box>
      </Box>
    );
  }

  const counts = getItemCounts();

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#f7fafc" }}>
      {/* Left menu */}
      <Box sx={{ width: 220, background: "#f5f5f5", p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Designer Portal</Typography>
        <Button fullWidth sx={{ mb: 1 }} onClick={() => navigate(`/applications/${applicationId}`)}>
          Dashboard
        </Button>
        <Button fullWidth sx={{ mb: 1 }}>My Projects</Button>
        <Button fullWidth sx={{ mb: 1 }}>Messages</Button>
        <Button fullWidth sx={{ mb: 1 }}>Settings</Button>
        <Button 
          fullWidth 
          sx={{ mb: 1, backgroundColor: '#e3f2fd', color: '#1976d2' }}
        >
          Work Items
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Status Summary in Sidebar */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Status Summary</Typography>
          {statusOptions.map(status => (
            <Box key={status.value} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {status.icon}
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {status.label}
                </Typography>
              </Box>
              <Badge badgeContent={counts[status.value]} color={status.color} />
            </Box>
          ))}
        </Box>
        
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => navigate(`/applications/${applicationId}`)}
        >
          Back to Application
        </Button>
      </Box>
      
      {/* Main content */}
      <Box sx={{ flex: 1, p: 4 }}>
        <Paper sx={{ p: 4, maxWidth: 900, mx: "auto", mt: 4 }}>
          {/* Header */}
          <Box mb={3}>
            <Typography variant="h4" component="h1" gutterBottom>
              Work Items - Application #{applicationId}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome, {user?.name || user?.username}
            </Typography>
          </Box>
          
          {/* Filters */}
          <Box sx={{ p: 2, mb: 3, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>Filters</Typography>
            <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {statusOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority}
                  onChange={(e) => setFilters({...filters, priority: e.target.value})}
                  label="Priority"
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {priorityOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Timing</InputLabel>
                <Select
                  value={filters.timing}
                  onChange={(e) => setFilters({...filters, timing: e.target.value})}
                  label="Timing"
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {timingOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  label="Category"
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {categoryOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Assignee</InputLabel>
                <Select
                  value={filters.assignee_id}
                  onChange={(e) => setFilters({...filters, assignee_id: e.target.value})}
                  label="Assignee"
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.hideCompleted}
                    onChange={(e) => setFilters({...filters, hideCompleted: e.target.checked})}
                  />
                }
                label="Hide Completed"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                onClick={() => setFilters({status: '', priority: '', assignee_id: '', timing: '', category: '', hideCompleted: false})}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Work Items */}
        <Grid container spacing={3}>
          {workItems.map(item => (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: isOverdue(item.due_date) ? '2px solid #f44336' : 'none',
                  position: 'relative',
                  backgroundColor: '#ebedefff'
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      <Chip
                        icon={getStatusIcon(item.status)}
                        label={statusOptions.find(opt => opt.value === item.status)?.label || item.status}
                        color={statusOptions.find(opt => opt.value === item.status)?.color || 'default'}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOverviewItem(item);
                          setOverviewStatusMenuAnchor(e.currentTarget);
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                      {item.category && (
                        <Chip
                          label={categoryOptions.find(opt => opt.value === item.category)?.label || item.category}
                          color="default"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Box>
                    <Chip
                      label={item.priority}
                      color={getPriorityColor(item.priority)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 1,
                      minHeight: 32, 
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      lineHeight: 1.2,
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => {
                      setSelectedItem(item);
                      setDetailDialogOpen(true);
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Box sx={{ mb: 1 }}>
                    {item.assignee_name && (
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Person fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {item.assignee_name}
                        </Typography>
                      </Box>
                    )}

                    {item.attachments && item.attachments.length > 0 && (
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <AttachFile fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {item.attachments.length} attachment(s)
                        </Typography>
                      </Box>
                    )}
                  </Box>

                </CardContent>

                <CardActions sx={{ pt: 0, pb: 1, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box display="flex" alignItems="center">
                    {item.timing && (
                      <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
                        <Schedule fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {item.timing}
                        </Typography>
                      </Box>
                    )}
                    {item.category && (
                      <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {categoryOptions.find(opt => opt.value === item.category)?.label || item.category}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  <Box>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedItem(item);
                        setDetailDialogOpen(true);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                      <Edit />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setItemToDelete(item);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Delete />
                    </IconButton>
                    {item.attachments && item.attachments.length > 0 && (
                      <Tooltip title="Download attachments">
                        <IconButton size="small">
                          <GetApp />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {workItems.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" color="text.secondary">
              No work items found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first work item to get started
            </Typography>
            <Button variant="contained" onClick={() => handleOpenDialog()}>
              Create Work Item
            </Button>
          </Paper>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => handleOpenDialog()}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16
          }}
        >
          <Add />
        </Fab>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingItem ? 'Edit Work Item' : 'Create Work Item'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                sx={{ mb: 2 }}
                required
              />
              
              <TextField
                fullWidth
                label="Description (Markdown supported)"
                multiline
                rows={6}
                value={formData.text}
                onChange={(e) => setFormData({...formData, text: e.target.value})}
                sx={{ mb: 2 }}
                required
                helperText="You can use Markdown formatting in the description"
              />
              
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                sx={{ mb: 2 }}
                placeholder="Additional notes or comments (Markdown supported)..."
                helperText="Optional notes - also supports Markdown formatting"
              />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Timing</InputLabel>
                    <Select
                      value={formData.timing}
                      onChange={(e) => setFormData({...formData, timing: e.target.value})}
                      label="Timing"
                    >
                      <MenuItem value=""><em>No timing</em></MenuItem>
                      {timingOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      label="Status"
                    >
                      {statusOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      label="Priority"
                    >
                      {priorityOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Timing</InputLabel>
                    <Select
                      value={formData.timing}
                      onChange={(e) => setFormData({...formData, timing: e.target.value})}
                      label="Timing"
                    >
                      {timingOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      label="Category"
                    >
                      <MenuItem value=""><em>Select Category</em></MenuItem>
                      {categoryOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Assignee</InputLabel>
                    <Select
                      value={formData.assignee_id}
                      onChange={(e) => setFormData({...formData, assignee_id: e.target.value})}
                      label="Assignee"
                    >
                      <MenuItem value=""><em>Unassigned</em></MenuItem>
                      {users.map(user => (
                        <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <input
                  accept="*"
                  style={{ display: 'none' }}
                  id="attachments-upload"
                  multiple
                  type="file"
                  onChange={(e) => setAttachments(Array.from(e.target.files))}
                />
                <label htmlFor="attachments-upload">
                  <Button variant="outlined" component="span" startIcon={<AttachFile />}>
                    Attach Files
                  </Button>
                </label>
                {attachments.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {attachments.length} file(s) selected
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={!formData.title.trim() || !formData.text.trim()}
            >
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Work Item</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this work item? This action cannot be undone.
            </Typography>
            {itemToDelete && (
              <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                "{itemToDelete.title}"
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detail View Dialog */}
        <Dialog 
          open={detailDialogOpen} 
          onClose={() => setDetailDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h5" component="div">
                {selectedItem?.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip
                  icon={getStatusIcon(selectedItem?.status)}
                  label={statusOptions.find(opt => opt.value === selectedItem?.status)?.label || selectedItem?.status}
                  color={statusOptions.find(opt => opt.value === selectedItem?.status)?.color || 'default'}
                  size="small"
                  onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label={selectedItem?.priority}
                  color={getPriorityColor(selectedItem?.priority)}
                  size="small"
                />
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedItem && (
              <Box>
                {/* Work Item Details */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {selectedItem.assignee_name && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center">
                        <Person fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Assignee:</strong> {selectedItem.assignee_name}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {selectedItem.timing && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center">
                        <Schedule fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Timing:</strong> {selectedItem.timing}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {selectedItem.category && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Category:</strong> {categoryOptions.find(opt => opt.value === selectedItem.category)?.label || selectedItem.category}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Created:</strong> {formatDate(selectedItem.created_at)}
                    </Typography>
                  </Grid>

                  {selectedItem.attachments && selectedItem.attachments.length > 0 && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center">
                        <AttachFile fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Attachments:</strong> {selectedItem.attachments.length} file(s)
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Markdown Content */}
                <Typography variant="h6" gutterBottom>Description</Typography>
                <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                  <ReactMarkdown>{selectedItem.text}</ReactMarkdown>
                </Paper>

                {/* Notes Section */}
                {selectedItem.notes && selectedItem.notes.trim() && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>Notes</Typography>
                    <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                      <ReactMarkdown>{selectedItem.notes}</ReactMarkdown>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            <Button 
              onClick={() => {
                setDetailDialogOpen(false);
                handleOpenDialog(selectedItem);
              }}
              variant="contained"
              startIcon={<Edit />}
            >
              Edit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Update Menu */}
        <Menu
          anchorEl={statusMenuAnchor}
          open={Boolean(statusMenuAnchor)}
          onClose={() => setStatusMenuAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {statusOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleQuickStatusUpdate(option.value)}
              disabled={updatingStatus || selectedItem?.status === option.value}
              sx={{ minWidth: 180 }}
            >
              <ListItemIcon>
                {getStatusIcon(option.value)}
              </ListItemIcon>
              <ListItemText 
                primary={option.label}
                sx={{ 
                  color: selectedItem?.status === option.value ? 'text.disabled' : 'inherit' 
                }}
              />
              {updatingStatus && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </MenuItem>
          ))}
        </Menu>

        {/* Overview Status Update Menu */}
        <Menu
          anchorEl={overviewStatusMenuAnchor}
          open={Boolean(overviewStatusMenuAnchor)}
          onClose={() => {
            setOverviewStatusMenuAnchor(null);
            setSelectedOverviewItem(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {statusOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleOverviewStatusUpdate(option.value)}
              disabled={updatingStatus || selectedOverviewItem?.status === option.value}
              sx={{ minWidth: 180 }}
            >
              <ListItemIcon>
                {getStatusIcon(option.value)}
              </ListItemIcon>
              <ListItemText 
                primary={option.label}
                sx={{ 
                  color: selectedOverviewItem?.status === option.value ? 'text.disabled' : 'inherit' 
                }}
              />
              {updatingStatus && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </MenuItem>
          ))}
        </Menu>
        </Paper>
      </Box>
    </Box>
  );
}

export default WorkItems;
